import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import authService from '../auth/authService';
import { getApiErrorMessage, NETWORK_ERROR_MESSAGE } from '../auth/apiError';
import Navbar from './Navbar';
import Sidebar from './Sidebar';
import './css/UserForm.css';
import { faTrashCan } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

const PROFILE_LOAD_ERROR = 'Your profile could not be loaded. Please refresh the page.';
const PROFILE_SAVE_ERROR = 'Your change could not be saved. Please try again.';
const ACCOUNT_DELETE_ERROR = 'Your account could not be deleted. Please try again.';

// Logs only the status or code: a request error's config carries the access token and the form.
const errorSummary = (error) => error?.response?.status ?? error?.code;

// The server's message for one field of a rejected save (DRF: {field: [text]}), if any.
const fieldErrorMessage = (err, field) => {
    const data = err && err.response && err.response.data;
    const value = data && typeof data === 'object' ? data[field] : null;
    const text = Array.isArray(value) ? value[0] : value;
    return typeof text === 'string' && text.trim() ? text : null;
};

const UserForm = ({ isCollapsed, toggleSidebar }) => {
    const [currentUser, setCurrentUser] = useState(null);
    const [activeTab, setActiveTab] = useState(() => {
        return sessionStorage.getItem('activeTab') || 'user';
    });
    
    // Tracks the currently edited field (only one at a time)
    const [editingField, setEditingField] = useState(null);
    
    const [formData, setFormData] = useState({
        name: '', surname: '', country: '', postal: '', phone: '',
        company: '', nip_vat: '', position: '',
        company_adress: '', company_country: '', company_postal: ''
    });

    // Stores the source of truth to revert to if the user clicks "Back"
    const [originalData, setOriginalData] = useState({});

    // Editing waits for the profile: a save sends the whole form, so saving a blank one
    // would wipe the other fields.
    const [profileLoaded, setProfileLoaded] = useState(false);
    const [profileError, setProfileError] = useState('');
    const [saveError, setSaveError] = useState('');

    // Password & Delete States
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [confirmDelete, setConfirmDelete] = useState(false);
    const [deletePassword, setDeletePassword] = useState('');
    const [deleteError, setDeleteError] = useState('');
    
    const [showPasswordChange, setShowPasswordChange] = useState(false);
    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [passwordError, setPasswordError] = useState('');
    const [passwordSuccess, setPasswordSuccess] = useState('');

    const navigate = useNavigate();

    useEffect(() => {
        const user = authService.getCurrentUser();
        setCurrentUser(user);
    }, []);

    useEffect(() => {
        if (currentUser) {
            authService.getUserProfile()
                .then(response => {
                    setFormData(response.data);
                    setOriginalData(response.data); // Set baseline data
                    setProfileLoaded(true);
                })
                .catch(error => {
                    console.error('Error fetching profile', errorSummary(error));
                    setProfileError(PROFILE_LOAD_ERROR);
                });
        }
    }, [currentUser]);


    useEffect(() => {
        sessionStorage.setItem('activeTab', activeTab);
    }, [activeTab]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    // --- NEW INLINE EDIT HANDLERS ---
    const handleEditClick = (fieldName) => {
        setSaveError('');
        setEditingField(fieldName);
    };

    const handleCancelEdit = () => {
        // Revert form data back to original state
        setFormData(prev => ({
            ...prev,
            [editingField]: originalData[editingField]
        }));
        setSaveError('');
        setEditingField(null);
    };

    const handleSaveField = () => {
        if (!currentUser || !profileLoaded) return;
    
        const profileData = {
            ...formData,
            is_filled: !!(formData.name && formData.surname && formData.country)
        };
    
        authService.updateUserProfile(profileData)
            .then(() => {
                const updatedUser = { ...currentUser, is_filled: true };
                localStorage.setItem('user', JSON.stringify(updatedUser));
                setOriginalData(formData); 
                setEditingField(null); 
                
                // ADD THIS: Refresh the page
                window.location.reload(); 
            })
            .catch(error => {
                console.error('Error saving profile', errorSummary(error));
                setSaveError(
                    fieldErrorMessage(error, editingField)
                    || (error && error.isAxiosError && !error.response ? NETWORK_ERROR_MESSAGE : PROFILE_SAVE_ERROR)
                );
            });
    };

    const handleDeleteAccount = () => {
        if (!currentUser) return;
        authService.deleteUserProfile(deletePassword)
            .then(() => {
                localStorage.removeItem('user');
                navigate('/login', { state: { message: 'Account has been terminated', messageType: 'success' } });
            })
            .catch(err => {
                setDeleteError(getApiErrorMessage(err, ACCOUNT_DELETE_ERROR));
            });
    };

    const handlePasswordSave = () => {
        if (!currentUser) return;
        authService.changePassword(currentPassword, newPassword)
            .then(() => {
                setPasswordSuccess('Password changed successfully.');
                setPasswordError('');
                setCurrentPassword('');
                setNewPassword('');
                
                setTimeout(() => {
                    setShowPasswordChange(false);
                    setPasswordSuccess('');
                    
                    // ADD THIS: Refresh the page after showing success message
                    window.location.reload(); 
                }, 1500);
            })
            .catch((err) => {
                // The server's texts here are written for users ("Old password is incorrect.").
                setPasswordError(getApiErrorMessage(err, 'Failed to change password.'));
                setPasswordSuccess('');
            });
    };

    // Helper function to render inline editable rows with a heading
    const renderField = (name, labelText) => {
        const isEditing = editingField === name;
        return (
            <div className="field-group" key={name}>
                {/* New Heading/Label */}
                <label className="field-label">{labelText}:</label>
                
                <div className="inline-edit-row">
                    <input
                        className={`inp-col ${isEditing ? 'is-editing' : 'is-readonly'}`}
                        name={name}
                        placeholder={labelText}
                        value={formData[name] || ''}
                        onChange={handleChange}
                        readOnly={!isEditing}
                    />
                    <div className="inline-btns">
                        {isEditing ? (
                            <>
                                <button type="button" className="inline-btn inline-save" onClick={handleSaveField}>Save</button>
                                <button type="button" className="inline-btn inline-back" onClick={handleCancelEdit}>Back</button>
                            </>
                        ) : profileLoaded ? (
                            <button type="button" className="inline-btn inline-edit" onClick={() => handleEditClick(name)}>Edit</button>
                        ) : null}
                    </div>
                </div>
                {isEditing && saveError && <span className="field-error" role="alert">{saveError}</span>}
            </div>
        );
    };

    return (
        <div className='lab-container'>
            <Navbar />
            <div className='lab-side-content'>
                <Sidebar active_n={1} isCollapsed={isCollapsed} toggleSidebar={toggleSidebar} />
                <div className='home-content'>
                    
                    <h2 className='aval-endp mar-usr-prof'>User Dashboard</h2>
                    {profileError && <p className='profile-error' role='alert'>{profileError}</p>}
                    
                    <div className="tab-switcher-container">
                        <div className="tab-switcher">
                            <button 
                                type="button" 
                                className={`tab-btn ${activeTab === 'user' ? 'active' : ''}`}
                                onClick={() => { setActiveTab('user'); setEditingField(null); }}
                            >
                                User Information
                            </button>
                            <div className="tab-divider"></div>
                            <button 
                                type="button" 
                                className={`tab-btn ${activeTab === 'company' ? 'active' : ''}`}
                                onClick={() => { setActiveTab('company'); setEditingField(null); }}
                            >
                                Company Details
                            </button>
                        </div>
                    </div>

                    <div className="profile-form">
                        <div className="tab-content">
                            
                            {activeTab === 'user' && (
                                <div className="user-section">
                                    <p className='ud-heading'>My Info</p>
                                    <div className="us-prof-bg-div flex-column-ud">
                                        {/* User Edit Fields */}
                                        {renderField('name', 'Name')}
                                        {renderField('surname', 'Surname')}
                                        {renderField('country', 'Country')}
                                        {renderField('postal', 'Postal Code')}
                                        {renderField('phone', 'Phone Number')}

                                        {/* Read-only email field */}
                                        {currentUser && (
                                            <div className="field-group">
                                                <label className="field-label">Email:</label>
                                                <div className="inline-edit-row">
                                                    <input 
                                                        className='inp-col is-readonly'
                                                        name="email" 
                                                        value={currentUser.email} 
                                                        readOnly 
                                                        title="Email cannot be changed"
                                                    />
                                                    <div className="inline-btns">
                                                        <span className="static-label">System</span>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    {/* PASSWORD FIELD */}
                                    <div className="field-group password-group">
                                        <label className="field-label">Password:</label>
                                        <div className="inline-edit-row" style={{ alignItems: showPasswordChange ? 'flex-start' : 'center' }}>
                                            
                                            {!showPasswordChange ? (
                                                // STATE 1: Read-only masked password
                                                <>
                                                    <input
                                                        className="inp-col is-readonly"
                                                        type="password"
                                                        value="********"
                                                        readOnly
                                                    />
                                                    <div className="inline-btns">
                                                        <button 
                                                            type="button" 
                                                            className="inline-btn inline-edit" 
                                                            onClick={() => setShowPasswordChange(true)}
                                                        >
                                                            Change
                                                        </button>
                                                    </div>
                                                </>
                                            ) : (
                                                // STATE 2: Editing (Current & New Password)
                                                <>
                                                    <div className="password-edit-inputs">
                                                        <input
                                                            className="inp-col is-editing"
                                                            type="password"
                                                            placeholder="Current Password"
                                                            value={currentPassword}
                                                            onChange={(e) => setCurrentPassword(e.target.value)}
                                                        />
                                                        <input
                                                            className="inp-col is-editing"
                                                            type="password"
                                                            placeholder="New Password"
                                                            value={newPassword}
                                                            onChange={(e) => setNewPassword(e.target.value)}
                                                        />
                                                        {passwordError && <span className="pass-msg error">{passwordError}</span>}
                                                        {passwordSuccess && <span className="pass-msg success">{passwordSuccess}</span>}
                                                    </div>
                                                    <div className="inline-btns">
                                                        <button
                                                            type="button"
                                                            className="inline-btn inline-save"
                                                            disabled={!currentPassword || !newPassword}
                                                            onClick={handlePasswordSave}
                                                            style={{ opacity: (!currentPassword || !newPassword) ? 0.5 : 1, cursor: (!currentPassword || !newPassword) ? 'not-allowed' : 'pointer' }}
                                                        >
                                                            Save
                                                        </button>
                                                        <button
                                                            type="button"
                                                            className="inline-btn inline-back"
                                                            onClick={() => {
                                                                setShowPasswordChange(false);
                                                                setCurrentPassword('');
                                                                setNewPassword('');
                                                                setPasswordError('');
                                                                setPasswordSuccess('');
                                                            }}
                                                        >
                                                            Back
                                                        </button>
                                                    </div>
                                                </>
                                            )}

                                        </div>
                                    </div>

                                    <div className="us-prof-bg-div flex-column-ud">
                                        <div className='del-btn-div'>
                                            <button type="button" onClick={() => setShowDeleteModal(true)} className="del-acc-btn">
                                                <FontAwesomeIcon className="us-pro-trash" icon={faTrashCan} />
                                                Delete Account and All Data
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {activeTab === 'company' && (
                                <div className="company-section">
                                    <p className='ud-heading'>Company Info</p>
                                    <div className="us-prof-bg-div flex-column-ud">
                                            <>
                                                {renderField('company', 'Company Name')}
                                                {renderField('nip_vat', 'VAT/NIP')}
                                                {renderField('position', 'Position')}
                                                {renderField('company_adress', 'Company Address')}
                                                {renderField('company_country', 'Company Country')}
                                                {renderField('company_postal', 'Company Postal Code')}
                                            </>
                                        
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    {showDeleteModal && (
                        <div className="modal-overlay-uf">
                            <div className="modal-uf">
                                <div className='conf-del-acc'><h3>Confirm Account Deletion</h3></div>             
                                <p className='p-del-acc'>This action is irreversible. Please confirm your decision.</p>
                                <div className='div-del-acc'>
                                    <label className=''>
                                        <input
                                        type="checkbox"
                                        checked={confirmDelete}
                                        onChange={(e) => setConfirmDelete(e.target.checked)}
                                        />
                                        {' '}Yes, I really want to delete my account and all data.
                                    </label>
                                    <input
                                        className='del-acc-pass'
                                        type="password"
                                        placeholder="Enter your password"
                                        value={deletePassword}
                                        onChange={(e) => setDeletePassword(e.target.value)}
                                    />
                                </div>
                                {deleteError && <p style={{ color: 'red' }}>{deleteError}</p>}
 
                                <div className="modal-buttons-uf">
                                    <button
                                    disabled={!confirmDelete || !deletePassword}
                                    onClick={handleDeleteAccount}
                                    >
                                    Delete
                                    </button>
                                    <button onClick={() => setShowDeleteModal(false)}>Cancel</button>
                                </div>
                            </div>
                        </div>
                        )}

                    
                </div>
            </div>
        </div>
    );
};

export default UserForm;